import { Injectable } from "@angular/core";
import { QueryStringObject } from "apps-module/app/shared-library/types/query-string-object";

@Injectable({
    providedIn: "root"
})
export class QueryStringSerializationService {
    private static get PROPERTY_NAMES_DELIMITER(): string {
        return ".";
    }

    public serialize(data: object): string {
        return this.serializeInternal(data);
    }

    private serializeInternal(data: object, prefix: string = ""): string {
        if (!Object.keys(data).length) {
            return "";
        }

        const queryParams: Array<string> = [];
        for (const propertyName of Object.keys(data)) {
            const propertyPath = prefix ? `${prefix}${propertyName}` : propertyName;
            const propertyValue = data[propertyName] as string | number | boolean;

            const queryParam = ((propertyValue !== null) && (typeof propertyValue === "object"))
                ? this.serializeInternal(propertyValue, `${propertyPath}${QueryStringSerializationService.PROPERTY_NAMES_DELIMITER}`)
                : `${encodeURIComponent(propertyPath)}=${encodeURIComponent(propertyValue || "")}`;

            queryParams.push(queryParam);
        }

        return queryParams.filter(queryParam => queryParam.split("=")[1]).join("&");
    }

    public deserialize(queryString: string): QueryStringObject {
        try {
            const resultObject = {};

            const spaceFormattedQueryString = queryString.replace(new RegExp("\\+", "g"), " ");

            for (const queryParam of spaceFormattedQueryString.split("&")) {
                const pair = decodeURIComponent(queryParam).split("=");

                const propertyPath = pair[0].split(QueryStringSerializationService.PROPERTY_NAMES_DELIMITER);
                const propertyValue = pair[1];

                if (propertyValue) {
                    this.setPropertyValue(resultObject, propertyPath, propertyValue);
                }
            }

            return resultObject;
        } catch (error) {
            return {};
        }
    }

    public deserializeToType<TResult>(queryString: string): TResult {
        try {
            const resultObject = {};

            for (const queryParam of queryString.split("&")) {
                const pair = decodeURIComponent(queryParam).split("=");

                const propertyPath = pair[0].split(QueryStringSerializationService.PROPERTY_NAMES_DELIMITER);
                const propertyValue = pair[1];

                if (propertyValue) {
                    this.setPropertyValue(resultObject, propertyPath, propertyValue);
                }
            }

            return resultObject as TResult;
        } catch (error) {
            return {} as TResult;
        }
    }

    private setPropertyValue(resultObject: object, propertyPath: Array<string>, propertyValue: string): void {
        if (!propertyPath.length) {
            return;
        }

        const propertyAlias = propertyPath[0];
        const isPlainProperty = propertyPath.length === 1;

        if (isPlainProperty) {
            resultObject[propertyAlias] = propertyValue;
            return;
        }

        let nestedObject: object = null;

        if (resultObject.hasOwnProperty(propertyAlias)) {
            nestedObject = resultObject[propertyAlias] as object;
        } else {
            const isArrayProperty = !isNaN(parseInt(propertyPath[1], 10));
            nestedObject = isArrayProperty ? [] : {};
            resultObject[propertyAlias] = nestedObject;
        }

        this.setPropertyValue(nestedObject, propertyPath.slice(1), propertyValue);
    }
}
